import { memo, useMemo } from 'react'
import { useIntl } from 'react-intl'
import BodyText from 'LuxKit/Typography/BodyText'
import type { GooglePlacesPriceLevel } from 'tripPlanner/types/common'

const PRICE_LEVEL_SYMBOLS: Partial<Record<GooglePlacesPriceLevel, string>> = {
  PRICE_LEVEL_INEXPENSIVE: '$',
  PRICE_LEVEL_MODERATE: '$$',
  PRICE_LEVEL_EXPENSIVE: '$$$',
  PRICE_LEVEL_VERY_EXPENSIVE: '$$$$',
} as const

export const shouldShowPriceLevel = (priceLevel?: GooglePlacesPriceLevel | null): boolean => {
  return !!priceLevel && priceLevel !== 'PRICE_LEVEL_UNSPECIFIED'
}

interface Props {
  priceLevel?: GooglePlacesPriceLevel | null
  size?: 'small' | 'medium' | 'large'
  colour?: App.Design.ContentColour
}

function GooglePlacePriceLevel({
  priceLevel,
  size = 'small',
  colour = 'neutral-two',
}: Props) {
  const intl = useIntl()

  const label = useMemo(() => {
    if (!priceLevel || priceLevel === 'PRICE_LEVEL_UNSPECIFIED') return null
    if (priceLevel === 'PRICE_LEVEL_FREE') {
      return intl.formatMessage({
        id: '4HYqu3BhD6GY4hBz_3i5',
        defaultMessage: 'Free',
        description: 'Price level label for free Google Places venues in the trip planner',
      })
    }
    return PRICE_LEVEL_SYMBOLS[priceLevel] ?? null
  }, [priceLevel, intl])

  if (!label) {
    return null
  }

  return (
    <BodyText
      variant={size}
      colour={colour}
      data-testid="google-place-price-level"
    >
      {label}
    </BodyText>
  )
}

export default memo(GooglePlacePriceLevel)
